import { cn } from "@/lib/utils";

// Semi-circular score gauge. The arc is always driven by `score` on a 0-100
// scale. By default it renders "{score}%" (CV review + public ATS check), but
// callers on a different scale (e.g. an interview /4 rubric) can override the
// displayed text via `displayValue`/`valueSuffix` and the color cut-offs via
// `thresholds`, while still passing a 0-100 value for the arc length.
export function ScoreGauge({
  score,
  label,
  displayValue,
  valueSuffix = "%",
  thresholds = { good: 80, ok: 60 },
}: {
  score: number;
  label: string;
  displayValue?: string;
  valueSuffix?: string;
  thresholds?: { good: number; ok: number };
}) {
  const getScoreColor = (value: number) => {
    if (value >= thresholds.good)
      return { stroke: "#22c55e", text: "text-green-600" };
    if (value >= thresholds.ok)
      return { stroke: "#eab308", text: "text-yellow-600" };
    return { stroke: "#ef4444", text: "text-red-600" };
  };

  const colors = getScoreColor(score);
  const radius = 80;
  const strokeWidth = 16;
  const circumference = Math.PI * radius;
  const progress = (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center justify-center">
      <p className="mb-4 text-center text-lg font-semibold text-text-heading">
        {label}
      </p>
      <div className="relative h-[130px] w-[208px]">
        <svg
          width="208"
          height="130"
          viewBox="0 0 208 130"
          className="overflow-visible"
        >
          <path
            d="M 20 110 A 80 80 0 0 1 188 110"
            fill="none"
            stroke="#e5e5e5"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          <path
            d="M 20 110 A 80 80 0 0 1 188 110"
            fill="none"
            stroke={colors.stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${progress} ${circumference}`}
            className="transition-all duration-700 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center pt-8">
          <span
            className={cn("text-5xl font-semibold tracking-tight", colors.text)}
          >
            {displayValue ?? score}
            {valueSuffix}
          </span>
        </div>
      </div>
    </div>
  );
}
